import { useField } from 'formik';
import { Suspense, lazy, memo, useEffect } from 'react';
import { Stack } from '@mui/material';
import { InterviewLanguage, Question, QuestionType } from '@synergetx/puppet-interview-types';

import PuppetQuestionFields from './PuppetQuestionFields';
import MultipleChoiceOptionFields from './MultipleChoiceOptionFields';
import ContinuePromptQuestionFields from './ContinuePromptQuestionFields';
import QuestionPromptField from './QuestionPromptField';
import LoadingPaper from '@components/LoadingPaper';

// Lazy: the rich-text editor and ProseMirror are only needed for info and continue questions.
const TextToHtmlTextfield = lazy(() => import('./TextToHtmlTextfield'));

interface Props {
    position: number;
    language: InterviewLanguage;
    type: QuestionType;
}

function QuestionFieldSet({ position, language, type }: Props) {
    const [{ value: question }, , { setValue: setQuestion }] = useField<
        Question['localeContent'][typeof language]
    >(`questions[${position}].localeContent.[${language}]`);

    const hasChoices = question && 'choices' in question;

    useEffect(() => {
        if (question && type === QuestionType.MULTIPLE && !hasChoices) {
            setQuestion({ ...question, choices: ['option1'] });
        }
    }, [hasChoices, question, setQuestion, type]);

    if (type === QuestionType.PUPPET) {
        return <PuppetQuestionFields position={position} language={language} />;
    } else if (type === QuestionType.INFO) {
        return (
            <Suspense fallback={<LoadingPaper loading />}>
                <TextToHtmlTextfield position={position} language={language} />
            </Suspense>
        );
    } else if (type === QuestionType.MULTIPLE && hasChoices) {
        return (
            <Stack direction="column" spacing={1}>
                <QuestionPromptField position={position} language={language} />
                <MultipleChoiceOptionFields position={position} language={language} />
            </Stack>
        );
    } else if (type === QuestionType.CONTINUE) {
        return <ContinuePromptQuestionFields language={language} position={position} />;
    } else {
        return (
            <Stack direction="column" spacing={1}>
                <QuestionPromptField position={position} language={language} />
            </Stack>
        );
    }
}

export default memo(QuestionFieldSet);
